import { ArrowDownOutlined, ArrowUpOutlined, LineChartOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { Button, Col, Row, Select, DatePicker, message, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { getJobApplicationsSource } from '../../utils/API';
import dayjs from 'dayjs';

const JobApplicationAnalytics = () => {
    const [source, setSource] = useState<any>([]);
    const [leadsByCategoryChartOptions, setLeadsByCategoryChartOptions] = useState<any>(null);
    const [leadsBarChartOptions, setLeadsBarChartOptions] = useState<any>(null); // Renamed for clarity
    const [leadsLineChartOptions, setLeadsLineChartOptions] = useState<any>(null); // New state for line chart
    const [metrics, setMetrics] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    // 🔹 Filters
    const [timeFilter, setTimeFilter] = useState<"today" | "thisMonth" | "lastMonth" | "custom" | undefined>(undefined);
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);
    const [tempStartDate, setTempStartDate] = useState<any>(null);
    const [tempEndDate, setTempEndDate] = useState<any>(null);
    const [showRangePicker, setShowRangePicker] = useState(false);

    const timeFilterOptions = [
        { value: 'today', label: 'Today' },
        { value: 'thisMonth', label: 'This Month' },
        { value: 'lastMonth', label: 'Last Month' },
        { value: 'custom', label: 'Custom Range' },
    ];

    const getCurrentTertial = () => {
        const month = dayjs().month();
        if (month >= 0 && month <= 3) return "jan-apr";
        if (month >= 4 && month <= 7) return "may-aug";
        return "sep-dec";
    };
    const [pipelineTimeframe, setPipelineTimeframe] = useState("full-year");
    const [pipelineChartOptions, setPipelineChartOptions] = useState<any>(null);

    const fetchLeads = async (params: any = {}) => {
        setLoading(true);
        try {
            const currentTimeFilter = params.timeFilter || timeFilter;
            const currentStartDate = startDate?.format?.('YYYY-MM-DD');
            const currentEndDate = endDate?.format?.('YYYY-MM-DD');
            const res = await getJobApplicationsSource(currentTimeFilter, currentStartDate, currentEndDate);

            // ✅ Filter only "JobApplication"
            const salesLeads = res.filter((lead: any) =>
                Array.isArray(lead.category) && lead.category.includes("JobApplication")
            );
            setSource(salesLeads);

            // --- Pie Chart ---
            const chartData = salesLeads.map((lead: any) => ({
                name: lead.sourceName || 'Unknown',
                value: lead.applicationCount || 0,
                color: lead.sourceName === "Company Website" ? "#FFD700" : "#98FB98",
            }));

            if (chartData.length === 0) {
                chartData.push({ name: "No Data", value: 1, color: "#98FB98" });
            }

            // Sort chartData by value (applications count) descending
            const sortedChartData = [...chartData].sort((a, b) => b.value - a.value);

            setLeadsByCategoryChartOptions({
                title: {
                    text: 'Applications by Source',
                    left: 'left',
                    textStyle: { fontSize: 16, fontWeight: 600 },
                },
                tooltip: {
                    trigger: 'item',
                    formatter: '{a} <br/>{b}: {c} ({d}%)',
                },
                legend: {
                    orient: "vertical",
                    right: 0,
                    top: "center",
                    formatter: (name: string) => {
                        const item = chartData.find((d: any) => d.name === name);
                        if (item) {
                            return `${name} | ${item.value?.toLocaleString()}`;
                        }
                        return name;
                    },
                    textStyle: { fontSize: 14, color: "#000" }
                },
                series: [
                    {
                        name: 'Applications by Source',
                        type: 'pie',
                        radius: ['40%', '70%'],
                        center: ['40%', '55%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: true,
                            color: '#000',
                            formatter: '{b}\n{c} ({d}%)',
                            fontSize: 13,
                            fontWeight: 'bold',
                        },
                        data: sortedChartData, // ✅ use sorted data
                    },
                ],
            });


            // --- Growth & Leads Enrichment ---
            const now = dayjs();
            const todayStart = now.startOf('day');
            const thisMonthStart = now.startOf('month');
            const lastMonthStart = now.subtract(1, 'month').startOf('month');
            const lastMonthEnd = now.subtract(1, 'month').endOf('month');

            const enrichedLeads = salesLeads.map((lead: any) => {
                let filteredLeads = lead.applicationCount || 0;
                let valueChange = 0;
                let growthPercentage = 0;

                if (currentTimeFilter === 'today') {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        dayjs(d).isSame(todayStart, 'day')
                    ).length;
                } else if (currentTimeFilter === 'thisMonth') {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        dayjs(d).isAfter(thisMonthStart) || dayjs(d).isSame(thisMonthStart, 'day')
                    ).length;
                } else if (currentTimeFilter === 'lastMonth') {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(lastMonthStart) || dayjs(d).isSame(lastMonthStart, 'day')) &&
                        (dayjs(d).isBefore(lastMonthEnd) || dayjs(d).isSame(lastMonthEnd, 'day'))
                    ).length;
                } else if (currentTimeFilter === 'custom' && currentStartDate && currentEndDate) {
                    filteredLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(dayjs(currentStartDate)) || dayjs(d).isSame(dayjs(currentStartDate), 'day')) &&
                        (dayjs(d).isBefore(dayjs(currentEndDate)) || dayjs(d).isSame(dayjs(currentEndDate), 'day'))
                    ).length;
                } else {
                    filteredLeads = lead.applicationCount || 0;
                }

                if (currentTimeFilter === 'lastMonth') {
                    valueChange = 0;
                    growthPercentage = 0;
                } else {
                    const thisMonthLeads = lead.applicationDates.filter((d: string) =>
                        dayjs(d).isAfter(thisMonthStart) || dayjs(d).isSame(thisMonthStart, 'day')
                    ).length;
                    const lastMonthLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(lastMonthStart) || dayjs(d).isSame(lastMonthStart, 'day')) &&
                        (dayjs(d).isBefore(lastMonthEnd) || dayjs(d).isSame(lastMonthEnd, 'day'))
                    ).length;
                    valueChange = thisMonthLeads - lastMonthLeads;
                    growthPercentage = lastMonthLeads > 0
                        ? Number(((valueChange / lastMonthLeads) * 100).toFixed(1))
                        : thisMonthLeads > 0 ? 100 : 0;
                }

                if (currentTimeFilter === 'custom' && currentStartDate && currentEndDate) {
                    const prevStartDate = dayjs(currentStartDate).subtract(
                        dayjs(currentEndDate).diff(dayjs(currentStartDate), 'day'), 'day'
                    );
                    const prevEndDate = dayjs(currentStartDate).subtract(1, 'day');
                    const currentLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(dayjs(currentStartDate)) || dayjs(d).isSame(dayjs(currentStartDate), 'day')) &&
                        (dayjs(d).isBefore(dayjs(currentEndDate)) || dayjs(d).isSame(dayjs(currentEndDate), 'day'))
                    ).length;
                    const prevLeads = lead.applicationDates.filter((d: string) =>
                        (dayjs(d).isAfter(prevStartDate) || dayjs(d).isSame(prevStartDate, 'day')) &&
                        (dayjs(d).isBefore(prevEndDate) || dayjs(d).isSame(prevEndDate, 'day'))
                    ).length;
                    valueChange = currentLeads - prevLeads;
                    growthPercentage = prevLeads > 0
                        ? Number(((valueChange / prevLeads) * 100).toFixed(1))
                        : currentLeads > 0 ? 100 : 0;
                }

                return {
                    ...lead,
                    filteredLeads,
                    valueChange,
                    growthPercentage,
                };
            });

            // --- Sort enrichedLeads by filteredLeads (high → low) ---
            const sortedLeads = [...enrichedLeads].sort(
                (a: any, b: any) => (b.filteredLeads || 0) - (a.filteredLeads || 0)
            );

            // --- Bar Chart Data ---
            const barChartCategories = sortedLeads.map((lead: any) => lead.sourceName || 'Unknown');
            const barChartValues = sortedLeads.map((lead: any) => lead.filteredLeads || 0);
            const growthPercentages = sortedLeads.map((lead: any) =>
                lead.growthPercentage >= 0
                    ? `↑ ${Math.abs(lead.growthPercentage)}%`
                    : `↓ ${Math.abs(lead.growthPercentage)}%`
            );

            setLeadsBarChartOptions({
                title: {
                    left: 'center',
                    textStyle: { fontSize: 16, fontWeight: 600 }
                },
                grid: { left: '5%', right: '10%', containLabel: true },
                xAxis: { type: 'value', show: false, splitLine: { show: false } },
                yAxis: [
                    {
                        type: 'category',
                        data: barChartCategories,
                        axisTick: { show: false },
                        axisLine: { show: true },
                        axisLabel: { fontSize: 14 },
                        inverse: true // 🔥 keeps highest at top
                    },
                    {
                        type: 'category',
                        position: 'right',
                        offset: 50,
                        data: growthPercentages,
                        axisLine: { show: false },
                        axisTick: { show: false },
                        axisLabel: {
                            fontSize: 14,
                            fontWeight: 'bold',
                            color: (val: string) => (val.includes('↓') ? 'red' : 'green')
                        },
                        inverse: true // 🔥 aligns with main axis
                    }
                ],
                series: [
                    {
                        type: 'bar',
                        data: barChartValues,
                        barWidth: 30,
                        barCategoryGap: '0%',
                        itemStyle: { color: '#409EFF' },
                        label: {
                            show: true,
                            position: 'right',
                            distance: 8,
                            formatter: '{c}',
                            fontSize: 14,
                            color: '#000'
                        }
                    }
                ]
            });

            // --- Collect unique dates across all sources ---
            const allDates = Array.from(
                new Set(
                    enrichedLeads.flatMap((lead: any) =>
                        lead.applicationDates.map((d: string) => dayjs(d).format("YYYY-MM-DD"))
                    )
                )
            ).sort(); // sorted list of all dates

            // --- Collect unique sources ---
            const allSources = Array.from(
                new Set(enrichedLeads.map((lead: any) => lead.sourceName || "Unknown"))
            );

            // --- Create one line series per source ---
            const lineSeries = allSources.map((source) => ({
                name: source,
                type: "line",
                smooth: true,
                symbol: "circle",
                lineStyle: { width: 2 },
                data: allDates.map((date) => {
                    const match = enrichedLeads.find(
                        (lead: any) =>
                            (lead.sourceName || "Unknown") === source &&
                            lead.applicationDates.some(
                                (d: string) => dayjs(d).format("YYYY-MM-DD") === date
                            )
                    );
                    return match ? match.applicationCount || 0 : 0;
                }),
            }));

            // --- Find max dynamically (across all series) ---
            const allValues = lineSeries.flatMap((s) => s.data);
            const maxValue = Math.max(...allValues, 0);

            setLeadsLineChartOptions({
                title: {
                    text: "Applications by Source",
                    left: "center",
                    textStyle: { fontSize: 16, fontWeight: 600 },
                },
                tooltip: { trigger: "axis" },
                legend: { top: 30, type: "scroll" },
                xAxis: {
                    type: "category",
                    data: allDates,
                    axisLabel: { rotate: allDates.length > 7 ? 45 : 0 },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    max: maxValue + 8, // dynamic
                    name: "Number of Applications",
                },
                series: lineSeries,
            });

            // --- Cards ---
            const dynamicMetrics = enrichedLeads
                .map((lead: any) => ({
                    title: lead.sourceName || "Unknown",
                    Total: lead.applicationCount || 0,
                    filteredLeads: lead.filteredLeads || 0,
                    valueChange: Math.abs(lead.valueChange),
                    change: {
                        text: `${Math.abs(lead.growthPercentage)}%`,
                        style: {
                            color: lead.growthPercentage >= 0 ? '#32CD32' : '#FF4500',
                            fontWeight: 'bold',
                        },
                        icon: lead.growthPercentage >= 0 ? (
                            <ArrowUpOutlined style={{ color: '#32CD32' }} />
                        ) : (
                            <ArrowDownOutlined style={{ color: '#FF4500' }} />
                        ),
                    },
                }))
                // ✅ Sort by applicationCount (Total) descending
                .sort((a: any, b: any) => b.Total - a.Total);

            setMetrics(dynamicMetrics);
            // --- Pipe graph: Applications Over Time (tertial bars) ---
            const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
            const tertialMonths: Array<{ monthIndex: number; label: string }> =
                pipelineTimeframe === "full-year" ? monthNames.map((label, i) => ({ monthIndex: i, label }))
                : pipelineTimeframe === "jan-apr" ? [{ monthIndex: 0, label: "January" }, { monthIndex: 1, label: "February" }, { monthIndex: 2, label: "March" }, { monthIndex: 3, label: "April" }]
                : pipelineTimeframe === "may-aug" ? [{ monthIndex: 4, label: "May" }, { monthIndex: 5, label: "June" }, { monthIndex: 6, label: "July" }, { monthIndex: 7, label: "August" }]
                : [{ monthIndex: 8, label: "September" }, { monthIndex: 9, label: "October" }, { monthIndex: 10, label: "November" }, { monthIndex: 11, label: "December" }];
            const pipePoints = tertialMonths.map((m) => ({ key: String(m.monthIndex), label: m.label, value: 0 }));
            enrichedLeads.forEach((lead: any) => {
                (lead.applicationDates || []).forEach((d: string) => {
                    const mi = dayjs(d).month();
                    const found = pipePoints.find((dp) => dp.key === String(mi));
                    if (found) found.value += 1;
                });
            });
            const pipeTotal = pipePoints.reduce((s: number, p: any) => s + p.value, 0);
            const allTimeTotal = salesLeads.reduce((s: number, l: any) => s + (l.applicationCount || (l.applicationDates || []).length), 0);
            setPipelineChartOptions({
                grid: { left: "3%", right: "3%", top: "22%", bottom: "5%", containLabel: true },
                title: { subtext: `In period: ${pipeTotal}  |  All time: ${allTimeTotal}`, left: "center", subtextStyle: { color: "#6B7280", fontSize: 12, fontWeight: 600 } },
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "shadow", shadowStyle: { color: "rgba(0, 114, 255, 0.06)" } },
                    backgroundColor: "rgba(15, 23, 42, 0.92)",
                    borderWidth: 0,
                    padding: [8, 12],
                    textStyle: { color: "#fff", fontSize: 12 },
                    formatter: (params: any) => `<span style="opacity:0.7">${params[0].name}</span><br/><b style="font-size:14px">${params[0].value} applications</b>`,
                },
                xAxis: { type: "category", data: pipePoints.map((d) => d.label), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: "#9ca3af", fontSize: 11 } },
                yAxis: { type: "value", axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: "#f3f4f6" } }, axisLabel: { color: "#9ca3af", fontSize: 11 } },
                series: [{
                    type: "bar",
                    data: pipePoints.map((d) => d.value),
                    barWidth: "45%",
                    showBackground: true,
                    backgroundStyle: { color: "rgba(0, 114, 255, 0.05)", borderRadius: [6, 6, 0, 0] },
                    itemStyle: {
                        borderRadius: [6, 6, 0, 0],
                        color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "#00c6ff" }, { offset: 1, color: "#0072ff" }] },
                    },
                    emphasis: { itemStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "#38e0ff" }, { offset: 1, color: "#0059ff" }] } } },
                    label: { show: true, position: "top", formatter: (params: any) => `${params.value}`, color: "#1f2937", fontWeight: "bold", fontSize: 11 },
                }],
            });


        } catch (err) {
            message.error("Failed to fetch JobApplicationSource");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeads();
    }, [timeFilter, startDate, endDate, pipelineTimeframe]);

    const sparkDataFor = (title: string) => {
        const sItem = source.find((x: any) => (x.sourceName || "Unknown") === title);
        return sItem ? sItem.applicationDates : [];
    };

    const totalApplications = metrics.reduce((s, m) => s + (m.Total || 0), 0);
    const totalFiltered = metrics.reduce((s, m) => s + (m.filteredLeads || 0), 0);
    const totalChange = metrics.reduce((s, m) => s + (m.valueChange || 0), 0);
    const allAppDates = source.flatMap((x: any) => x.applicationDates || []);

    const KPI_ACCENTS = ["#EF4444", "#14B8A6", "#8B5CF6", "#F59E0B", "#22C55E", "#0EA5E9", "#EC4899"];

    const sparklineOption = (dates: string[], color: string) => {
        const buckets: Record<string, number> = {};
        (dates || []).forEach((d) => {
            const key = dayjs(d).format("YYYY-MM-DD");
            buckets[key] = (buckets[key] || 0) + 1;
        });
        const sorted = Object.keys(buckets).sort();
        const data = sorted.map((k) => buckets[k]);
        return {
            grid: { left: 0, right: 0, top: 2, bottom: 2 },
            xAxis: { type: "category", show: false, data: sorted },
            yAxis: { type: "value", show: false },
            series: [{
                type: "line",
                data: data.length ? data : [0, 0],
                smooth: true,
                symbol: "none",
                lineStyle: { width: 2, color },
                areaStyle: { color, opacity: 0.15 },
            }],
        };
    };

    return (
        <div className="la-page-wrap" style={{ position: "relative", padding: 16 }}>
            <style>{`
                .la-card { transition: box-shadow 200ms ease, transform 200ms ease; }
                .la-card:hover { box-shadow: 0 8px 24px rgba(16,24,40,0.08) !important; transform: translateY(-2px); }
                .la-kpi-card { transition: box-shadow 200ms ease, transform 200ms ease; }
                .la-kpi-card:hover { box-shadow: 0 8px 24px rgba(16,24,40,0.08) !important; transform: translateY(-2px); }
                .la-bar-fill { transition: width 600ms ease; }
                @media (max-width: 992px) {
                    .la-kpi-sources { grid-template-columns: repeat(2, 1fr) !important; }
                }
                @media (max-width: 640px) {
                    .la-kpi-grid { grid-template-columns: 1fr !important; }
                    .la-kpi-hero { grid-row: span 1 !important; }
                    .la-kpi-sources { grid-template-columns: 1fr !important; }
                }
            `}</style>

            <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                <Col xs={20} sm={20} md={20} lg={20}>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>Job Application Analytics</h2>
                </Col>
                <Col xs={4} sm={4} md={4} lg={4}>
                    <div className="flex justify-end">
                        <Select
                            style={{ width: 180 }}
                            value={timeFilter}
                            onChange={(value) => {
                                setTimeFilter(value);
                                if (value === "custom") {
                                    setShowRangePicker(true);
                                } else {
                                    setStartDate(null);
                                    setEndDate(null);
                                    setTempStartDate(null);
                                    setTempEndDate(null);
                                    setShowRangePicker(false);
                                }
                            }}
                            placeholder="Select Time Filter"
                            allowClear
                            options={timeFilterOptions}
                            dropdownRender={(menu) => (
                                <div>
                                    {menu}
                                    <div style={{ textAlign: "center" }}>
                                        <Button
                                            type="link"
                                            style={{ color: "red" }}
                                            onClick={() => {
                                                setTimeFilter(undefined);
                                                setStartDate(null);
                                                setEndDate(null);
                                                setTempStartDate(null);
                                                setTempEndDate(null);
                                                setShowRangePicker(false);
                                                fetchLeads();
                                            }}
                                        >
                                            Clear All Filters
                                        </Button>
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                </Col>
            </Row>

            {showRangePicker && (
                <div
                    style={{
                        position: "fixed",
                        top: "25%",
                        left: "70%",
                        transform: "translate(-10%, -50%)",
                        zIndex: 1500,
                        background: "#fff",
                        padding: "16px",
                        boxShadow:
                            "0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08), 0 9px 28px 8px rgba(0,0,0,0.05)",
                        borderRadius: "8px",
                        width: "fit-content",
                    }}
                >
                    <DatePicker.RangePicker
                        value={tempStartDate && tempEndDate ? [tempStartDate, tempEndDate] : null}
                        onChange={(dates) => {
                            setTempStartDate(dates ? dates[0] : null);
                            setTempEndDate(dates ? dates[1] : null);
                        }}
                        format="YYYY-MM-DD"
                        style={{ marginBottom: "16px", width: "100%" }}
                        allowClear={false}
                        getPopupContainer={(triggerNode) => document.body}
                        popupStyle={{ zIndex: 2000 }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                        <Button
                            onClick={() => {
                                setShowRangePicker(false);
                                setTempStartDate(null);
                                setTempEndDate(null);
                                setTimeFilter(undefined);
                                setStartDate(null);
                                setEndDate(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            style={{ color: "white" }}
                            className="allbuttons"
                            onClick={() => {
                                if (tempStartDate && tempEndDate) {
                                    setStartDate(tempStartDate);
                                    setEndDate(tempEndDate);
                                    setShowRangePicker(false);
                                }
                            }}
                            disabled={!tempStartDate || !tempEndDate}
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            )}

            {/* ---------- KPI SECTION (hero spans 2 rows left, sources 2-row grid right) ---------- */}
            <div className="la-kpi-grid" style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "minmax(240px, 1fr) 3fr", gap: 20 }}>
                {/* Hero: Total Applications spans 2 rows */}
                {loading ? (
                    <div style={{ padding: 24, borderRadius: 20, background: "#fff", border: "1px solid #EAECEF", minHeight: "100%", display: "flex", alignItems: "center" }}>
                        <Skeleton active paragraph={{ rows: 4 }} />
                    </div>
                ) : (
                    <div
                        className="la-kpi-card la-kpi-hero"
                        style={{
                            padding: 24, borderRadius: 20, background: "#fff",
                            border: "1px solid #EAECEF", boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
                            height: "100%", display: "flex", flexDirection: "column", gap: 12, gridRow: "span 2",
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", letterSpacing: "0.01em" }}>Total Applications</span>
                            <span style={{ width: 40, height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: `${"#EF4444"}12`, color: "#EF4444" }}>
                                <LineChartOutlined style={{ fontSize: 20 }} />
                            </span>
                        </div>
                        <div style={{ fontSize: 34, fontWeight: 800, color: "#111827", lineHeight: 1, letterSpacing: "-0.02em" }}>
                            {Number(totalApplications).toLocaleString("en-IN")}
                        </div>
                        <div className="flex items-center justify-between">
                            <span style={{ fontSize: 14, color: "#6B7280", fontWeight: 600 }}>
                                {Number(totalFiltered).toLocaleString("en-IN")} in period
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: totalChange >= 0 ? "#22C55E" : "#EF4444", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                {totalChange >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                {Math.abs(totalChange)}
                            </span>
                        </div>
                        <div style={{ flex: 1, minHeight: 40, marginTop: 4 }}>
                            <ReactECharts option={sparklineOption(allAppDates, "#EF4444")} style={{ height: "100%", width: "100%" }} />
                        </div>
                    </div>
                )}
                {/* Right: 2-row grid of source KPI cards (3 per row on lg, 2 on sm) */}
                <div className="la-kpi-sources" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, alignContent: "stretch" }}>
                    {loading
                        ? Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} style={{ padding: 20, borderRadius: 20, background: "#fff", border: "1px solid #EAECEF", minHeight: 120 }}>
                                <Skeleton active paragraph={{ rows: 2 }} title={{ width: "50%" }} />
                            </div>
                        ))
                        : [...metrics].sort((a, b) => (b.Total || 0) - (a.Total || 0)).slice(0, 6).map((metric: any, index: number) => {
                            const accent = KPI_ACCENTS[index % KPI_ACCENTS.length];
                            const dates = sparkDataFor(metric.title);
                            return (
                                <div
                                    key={metric.title}
                                    className="la-kpi-card"
                                    style={{
                                        padding: 20, borderRadius: 20, background: "#fff",
                                        border: "1px solid #EAECEF", boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
                                        display: "flex", flexDirection: "column", gap: 8, minHeight: 120,
                                    }}
                                >
                                    <div className="flex items-center justify-between">
                                        <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }}>{metric.title}</span>
                                        <span style={{ width: 34, height: 34, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: `${accent}12`, color: accent, flexShrink: 0 }}>
                                            <LineChartOutlined style={{ fontSize: 17 }} />
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 26, fontWeight: 800, color: "#111827", lineHeight: 1, letterSpacing: "-0.02em" }}>
                                        {Number(metric.Total || 0).toLocaleString("en-IN")}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
                                            {metric.filteredLeads} in period
                                        </span>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: metric.change.style.color, display: "inline-flex", alignItems: "center", gap: 3 }}>
                                            {metric.change.icon}
                                            {metric.change.text}
                                        </span>
                                    </div>
                                    <div style={{ height: 24, marginTop: 2 }}>
                                        <ReactECharts option={sparklineOption(dates, accent)} style={{ height: "100%", width: "100%" }} />
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>

            {/* ---------- CHART SECTION (60 / 40) ---------- */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {/* Left: Application Source Analysis (thin progress bars) */}
                <Col xs={24} lg={14}>
                    <div className="la-card" style={{ padding: 24, borderRadius: 20, background: "#fff", border: "1px solid #EAECEF", boxShadow: "0 1px 3px rgba(16,24,40,0.04)", height: "100%" }}>
                        <div className="flex items-center justify-between" style={{ marginBottom: 24 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>Application Source Analysis</h3>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 8, padding: "4px 10px" }}>Real-time</span>
                        </div>
                        {loading ? (
                            <Skeleton active paragraph={{ rows: 6 }} />
                        ) : (
                            (() => {
                                const sorted = [...metrics].sort((a, b) => (b.Total || 0) - (a.Total || 0));
                                const totalVal = sorted.reduce((s, m) => s + (m.Total || 0), 0);
                                const maxVal = Math.max(...sorted.map((m) => m.Total || 0), 1);
                                return (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                                        {sorted.map((m: any) => {
                                            const widthPct = Math.max(((m.Total || 0) / maxVal) * 100, (m.Total || 0) === 0 ? 1.5 : 2);
                                            const fill = "#94A3B8";
                                            return (
                                                <div key={m.title} className="flex items-center" style={{ gap: 14 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", width: 90, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.title}</span>
                                                    <div style={{ flex: 1, height: 8, borderRadius: 999, background: "#EEF2F7", overflow: "hidden" }}>
                                                        <div className="la-bar-fill" style={{ width: `${widthPct}%`, height: "100%", borderRadius: 999, background: fill, transition: "width 600ms ease" }} />
                                                    </div>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", width: 72, flexShrink: 0, textAlign: "right" }}>{Number(m.Total || 0).toLocaleString("en-IN")}</span>
                                                </div>
                                            );
                                        })}
                                        <div style={{ marginTop: 6, paddingTop: 18, borderTop: "1px solid #EEF2F7", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280" }}>Total Applications</span>
                                            <span style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>{Number(totalVal).toLocaleString("en-IN")}</span>
                                        </div>
                                    </div>
                                );
                            })()
                        )}
                    </div>
                </Col>

                {/* Right: Applications by Source (bar chart) */}
                <Col xs={24} lg={10}>
                    <div className="la-card" style={{ padding: 24, borderRadius: 18, background: "#fff", border: "1px solid #EAECEF", boxShadow: "0 1px 3px rgba(16,24,40,0.04)", height: "100%", display: "flex", flexDirection: "column" }}>
                        <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>Applications by Source</h3>
                        </div>
                        <div style={{ flex: 1, minHeight: 260 }}>
                            {leadsBarChartOptions ? (
                                <ReactECharts option={leadsBarChartOptions} style={{ height: "100%", width: "100%" }} />
                            ) : (
                                <Skeleton active paragraph={{ rows: 6 }} />
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {/* ---------- DONUT + LINE ---------- */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} lg={12}>
                    <div className="la-card" style={{ padding: 24, borderRadius: 18, background: "#fff", border: "1px solid #EAECEF", boxShadow: "0 1px 3px rgba(16,24,40,0.04)", height: "100%" }}>
                        <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>Application Distribution</h3>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 8, padding: "4px 10px" }}>Share</span>
                        </div>
                        {loading ? (
                            <Skeleton active paragraph={{ rows: 5 }} />
                        ) : leadsByCategoryChartOptions ? (
                            <ReactECharts option={leadsByCategoryChartOptions} style={{ height: 320, width: "100%" }} />
                        ) : (
                            <Skeleton active paragraph={{ rows: 5 }} />
                        )}
                    </div>
                </Col>
                <Col xs={24} lg={12}>
                    <div className="la-card" style={{ padding: 24, borderRadius: 18, background: "#fff", border: "1px solid #EAECEF", boxShadow: "0 1px 3px rgba(16,24,40,0.04)", height: "100%" }}>
                        <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>Applications Over Time</h3>
                            <Select
                                value={pipelineTimeframe}
                                onChange={(val) => setPipelineTimeframe(val)}
                                style={{ width: 130 }}
                                size="small"
                                options={[
                                    { value: "full-year", label: "Full Year" },
                                    { value: "jan-apr", label: "Jan-Apr" },
                                    { value: "may-aug", label: "May-Aug" },
                                    { value: "sep-dec", label: "Sep-Dec" },
                                ]}
                            />
                        </div>
                        <div style={{ minHeight: 320 }}>
                            {pipelineChartOptions ? (
                                <ReactECharts option={pipelineChartOptions} style={{ height: 320, width: "100%" }} />
                            ) : (
                                <Skeleton active paragraph={{ rows: 5 }} />
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};


export default JobApplicationAnalytics;